/**
 * นำเข้าพนักงาน — ทะเบียนเต็ม (ไม่รวมเงินเดือน)
 * อัปไฟล์ Excel/CSV (รองรับหลายชีต) → ระบบจับหัวคอลัมน์ + แปลงข้อมูลให้ตรงระบบ
 * (เบอร์ 0xxxxxxxxx · วันที่ YYYY-MM-DD · รหัสธนาคาร · เข้ารหัสเลขบัญชี) →
 * ออกรหัสพนักงาน MM### ต่อจากที่มีอยู่ → ตรวจก่อนยืนยัน (Preview) → นำเข้าทีเดียว.
 * เงินเดือนตั้งทีหลังที่ "ตั้งฐานเงินเดือน". ดู endpoint POST /hr/employees/bulk-import
 *
 * หัวคอลัมน์ที่รองรับ (ตรงกับเทมเพลตที่ลูกค้าใช้):
 *   displayName, relationship, firstName, lastName, phonePrefix, phoneNumber,
 *   emergencyPrefix, emergencyNumber, email, address, bankName, accountNumber,
 *   departmentName, roleName, level, contractType, joinedDate
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, Users } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useEmployees, useBulkImportEmployees, type BulkImportEmployee } from '../../hooks';
import { useToast } from '../../components/ui/toast';

// รหัสธนาคารที่ระบบรองรับ (ตรงกับ dropdown หน้าพนักงาน)
const BANK_CODE: Record<string, string> = {
  kbank: 'KBANK', bbl: 'BBL', scb: 'SCB', ktb: 'KTB',
  ttb: 'TTB', tmb: 'TTB', bay: 'BAY', gsb: 'GSB', baac: 'BAAC',
};
const TITLES = ['นาย', 'นาง', 'นางสาว', 'น.ส.', 'ด.ช.', 'ด.ญ.'];

const norm = (v: unknown) => String(v == null ? '' : v).replace(/\s+/g, ' ').trim();

function normPhone(prefix: unknown, num: unknown): { val: string; bad: boolean } {
  let n = norm(num).replace(/\D/g, '');
  if (!n) return { val: '', bad: false };
  if (!n.startsWith('0')) n = '0' + n.replace(/^66/, ''); // prefix 66 → local
  const ok = /^0\d{9}$/.test(n) || /^0\d{8}$/.test(n); // มือถือ 10 / บ้าน 9
  return { val: ok ? n : '', bad: !ok };
}

function normDate(raw: unknown): { val: string; flag: string } {
  const s = norm(raw);
  if (!s) return { val: '', flag: '' };
  let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); // D/M/YYYY
  if (m) {
    let d = +m[1]!, mo = +m[2]!; const y = +m[3]!;
    if (mo > 12 && d <= 12) { [d, mo] = [mo, d]; }
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return { val: '', flag: 'วันที่ไม่ถูกต้อง: ' + s };
    return { val: `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`, flag: '' };
  }
  m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/); // YYYY-MM-DD หรือ YYYY-DD-MM
  if (m) {
    const y = +m[1]!; let mo = +m[2]!; let d = +m[3]!; let flag = '';
    if (mo > 12 && d <= 12) { [mo, d] = [d, mo]; flag = 'สลับ วัน/เดือน ให้แล้ว (' + s + ')'; }
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return { val: '', flag: 'วันที่ไม่ถูกต้อง: ' + s };
    return { val: `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`, flag };
  }
  return { val: '', flag: 'อ่านวันที่ไม่ออก: ' + s };
}

interface ParsedRow extends BulkImportEmployee {
  sheet: string;
  issues: string[];
  dup?: boolean;
}

const REQUIRED_HEADERS = ['firstName', 'lastName', 'displayName'];

function rowsFromSheet(aoa: string[][], sheet: string): Array<Record<string, string>> {
  if (!aoa.length) return [];
  const head = (aoa[0] ?? []).map(norm);
  // ชีตข้อมูลต้องมีหัวคอลัมน์หลัก (กันชีต lookup เช่น bankname)
  const hit = REQUIRED_HEADERS.filter((h) => head.includes(h)).length;
  if (hit < 2) return [];
  const idx: Record<string, number> = {};
  head.forEach((h, i) => { if (h) idx[h] = i; });
  const out: Array<Record<string, string>> = [];
  for (let r = 1; r < aoa.length; r++) {
    const row = aoa[r] ?? [];
    const rec: Record<string, string> = { __sheet: sheet };
    for (const [k, i] of Object.entries(idx)) rec[k] = norm(row[i]);
    if (!rec.firstName && !rec.lastName && !rec.displayName) continue; // แถวว่าง
    out.push(rec);
  }
  return out;
}

function nextEmpSeq(existing: Array<{ emp_code?: string | null }>): number {
  let max = 0;
  for (const e of existing) {
    const m = /^MM(\d+)$/.exec(e.emp_code ?? '');
    if (m) max = Math.max(max, +m[1]!);
  }
  return max + 1;
}

export function ImportEmployees() {
  const toast = useToast();
  // เช็ครหัสซ้ำต้องเห็นทุกคนรวมคนออกแล้ว — กันนำเข้าทับรหัสที่เคยมี
  const { data: existing } = useEmployees(true, true);
  const importM = useBulkImportEmployees();

  const [fileName, setFileName] = useState('');
  const [raw, setRaw] = useState<Array<Record<string, string>>>([]);
  const [result, setResult] = useState<{ written: number; failed: number; errors: Array<{ emp_code: string; msg: string }> } | null>(null);

  const existingList = (existing as Array<{ emp_code?: string | null; first_name?: string; last_name?: string }> | undefined) ?? [];

  const parsed = useMemo<ParsedRow[]>(() => {
    if (!raw.length) return [];
    const startSeq = nextEmpSeq(existingList);
    const existingNames = new Set(existingList.map((e) => `${norm(e.first_name)}|${norm(e.last_name)}`));
    const seenNames = new Set<string>();
    return raw.map((rec, i) => {
      const first = norm(rec.firstName);
      const last = norm(rec.lastName);
      const nick = norm(rec.displayName);
      const phone = normPhone(rec.phonePrefix, rec.phoneNumber);
      const emerg = normPhone(rec.emergencyPrefix, rec.emergencyNumber);
      const email = norm(rec.email);
      const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
      const bankRaw = norm(rec.bankName).toLowerCase();
      const bank = !bankRaw || bankRaw === '-' ? '' : (BANK_CODE[bankRaw] ?? 'OTHER');
      const acctRaw = norm(rec.accountNumber);
      const acctBad = !!acctRaw && !/^[0-9-]+$/.test(acctRaw);
      const acct = acctBad ? '' : acctRaw.replace(/[^0-9-]/g, '');
      const d = normDate(rec.joinedDate);

      const issues: string[] = [];
      if (!last) issues.push('ไม่มีนามสกุล');
      if (TITLES.includes(first)) issues.push('คำนำหน้าปนในชื่อ');
      if (phone.bad) issues.push('เบอร์ไม่ถูกต้อง → เว้นว่าง');
      if (email && !emailOk) issues.push('อีเมลผิดรูป → เว้นว่าง');
      if (acctBad) issues.push(`เลขบัญชีไม่ใช่ตัวเลข ("${acctRaw}") → เว้นว่าง`);
      if (!acctRaw) issues.push('ไม่มีเลขบัญชี');
      if (d.flag) issues.push('วันที่: ' + d.flag);
      if (!d.val) issues.push('ไม่มีวันเริ่มงาน');

      const nameKey = `${first}|${last}`;
      const dup = (!!last && existingNames.has(nameKey)) || (!!last && seenNames.has(nameKey));
      if (last) seenNames.add(nameKey);
      if (dup) issues.push('ชื่อซ้ำกับที่มีอยู่/ในไฟล์');

      return {
        sheet: rec.__sheet || '',
        emp_code: 'MM' + String(startSeq + i).padStart(3, '0'),
        first_name: first,
        last_name: last,
        nickname: nick,
        phone: phone.val,
        emergency_phone: emerg.val,
        email: emailOk ? email : '',
        address_current: norm(rec.address),
        bank,
        bank_account_full: acct,
        department: norm(rec.departmentName),
        position: norm(rec.roleName),
        start_date: d.val,
        status: 'active',
        issues,
        dup,
      } as ParsedRow;
    });
  }, [raw, existingList]);

  const bySheet = useMemo(() => {
    const m: Record<string, number> = {};
    parsed.forEach((p) => { m[p.sheet || '(ไม่ระบุชีต)'] = (m[p.sheet || '(ไม่ระบุชีต)'] || 0) + 1; });
    return m;
  }, [parsed]);

  const issueRows = parsed.filter((p) => p.issues.length > 0);
  const dupCount = parsed.filter((p) => p.dup).length;

  async function onFile(file: File) {
    setFileName(file.name);
    setResult(null);
    try {
      const XLSX = await import('xlsx');
      let sheets: Array<{ name: string; aoa: string[][] }> = [];
      if (/\.(xlsx|xls)$/i.test(file.name)) {
        const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
        sheets = wb.SheetNames.map((sn) => ({
          name: sn.trim(),
          aoa: XLSX.utils.sheet_to_json<string[]>(wb.Sheets[sn]!, { header: 1, raw: false, defval: '' }),
        }));
      } else {
        const wb = XLSX.read(await file.text(), { type: 'string' });
        const sn = wb.SheetNames[0]!;
        sheets = [{ name: sn.trim(), aoa: XLSX.utils.sheet_to_json<string[]>(wb.Sheets[sn]!, { header: 1, raw: false, defval: '' }) }];
      }
      const all = sheets.flatMap((s) => rowsFromSheet(s.aoa, s.name));
      if (!all.length) {
        toast.error('ไม่พบข้อมูลพนักงานในไฟล์ — ต้องมีหัวคอลัมน์ firstName / lastName / displayName');
        setRaw([]);
        return;
      }
      setRaw(all);
    } catch {
      toast.error('อ่านไฟล์ไม่สำเร็จ — ลองบันทึกเป็น .xlsx หรือ .csv แล้วอัปใหม่');
      setRaw([]);
    }
  }

  function runImport() {
    if (!parsed.length) return;
    const payload: BulkImportEmployee[] = parsed.map(({ sheet: _s, issues: _i, dup: _d, ...rest }) => rest);
    importM.mutate(payload, {
      onSuccess: (r) => {
        setResult(r);
        toast.success(`นำเข้าแล้ว ${r.written} คน${r.failed ? ` · ไม่สำเร็จ ${r.failed}` : ''}`);
        setRaw([]);
        setFileName('');
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'นำเข้าไม่สำเร็จ'),
    });
  }

  return (
    <AdminLayout title="นำเข้าพนักงาน (ทะเบียนเต็ม)" subtitle="อัปไฟล์ Excel/CSV ครั้งเดียว ได้ทั้งบริษัท — ตำแหน่ง·แผนก·เบอร์·เลขบัญชี·วันเริ่มงาน (เงินเดือนตั้งทีหลัง)">
      <div className="max-w-4xl mx-auto space-y-4 pb-12">
        <p className="card p-3 text-sm text-gray-600 bg-blue-50 border-blue-100">
          รองรับไฟล์หลายชีต (เช่น แยกตามทีม) — ระบบจะรวมทุกชีตที่มีหัวคอลัมน์พนักงานให้อัตโนมัติ
          และข้ามชีตที่เป็นตารางอ้างอิง. ระบบจะออก<b>รหัสพนักงาน MM### ต่อจากเลขล่าสุด</b> ให้เอง
          แล้วให้ตรวจก่อนยืนยันเสมอ.
        </p>

        {/* อัปไฟล์ */}
        <div className="card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <label className="label mb-0">เลือกไฟล์พนักงาน (.xlsx / .csv)</label>
            <label className="btn-primary inline-flex items-center gap-1.5 cursor-pointer">
              <FileSpreadsheet className="w-4 h-4" /> เลือกไฟล์
              <input type="file" accept=".xlsx,.xls,.csv" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />
            </label>
          </div>
          {fileName && <p className="text-xs text-gray-500">ไฟล์: {fileName}</p>}
          {parsed.length > 0 && (
            <p className="text-sm text-teal-700">
              อ่านได้ <b>{parsed.length} คน</b> · {Object.entries(bySheet).map(([s, n]) => `${s} ${n}`).join(' · ')}
            </p>
          )}
        </div>

        {/* ผลตรวจ */}
        {parsed.length > 0 && (
          <div className="card p-4 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-sm">
              <Stat label="พนักงานทั้งหมด" value={parsed.length} tone="ok" />
              <Stat label="มีเลขบัญชี" value={parsed.filter((p) => p.bank_account_full).length} tone="muted" />
              <Stat label="แถวมีข้อสังเกต" value={issueRows.length} tone={issueRows.length ? 'warn' : 'ok'} />
              <Stat label="ชื่อซ้ำ" value={dupCount} tone={dupCount ? 'warn' : 'ok'} />
            </div>

            <p className="text-xs text-amber-700 bg-amber-50 rounded p-2 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              ไฟล์นี้ไม่มีเลขบัตรประชาชน — นำเข้าได้ แต่พนักงานจะผูกบัญชีไลน์เองด้วยเลขบัตรไม่ได้จนกว่าจะเติมภายหลัง
              (ที่หน้ารายชื่อพนักงาน). เงินเดือนยังไม่ถูกตั้งในขั้นนี้.
            </p>

            {/* ตารางตัวอย่าง */}
            <div className="rounded-lg border border-gray-200 overflow-x-auto">
              <table className="w-full text-xs whitespace-nowrap">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    {['รหัส', 'ชื่อเล่น', 'ชื่อ-สกุล', 'เบอร์', 'แผนก', 'ตำแหน่ง', 'เริ่มงาน', 'ธนาคาร', 'บัญชี (ท้าย)'].map((h) => (
                      <th key={h} className="px-2 py-1.5 text-left font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parsed.map((p) => (
                    <tr key={p.emp_code} className={`border-t border-gray-100 ${p.dup ? 'bg-amber-50/40' : ''}`}>
                      <td className="px-2 py-1 font-mono">{p.emp_code}</td>
                      <td className="px-2 py-1">{p.nickname}</td>
                      <td className="px-2 py-1">{p.first_name} {p.last_name}</td>
                      <td className="px-2 py-1 font-mono">{p.phone || <span className="text-gray-300">—</span>}</td>
                      <td className="px-2 py-1">{p.department}</td>
                      <td className="px-2 py-1">{p.position}</td>
                      <td className="px-2 py-1 font-mono">{p.start_date || <span className="text-gray-300">—</span>}</td>
                      <td className="px-2 py-1">{p.bank}</td>
                      <td className="px-2 py-1 font-mono">{p.bank_account_full ? '••' + p.bank_account_full.replace(/-/g, '').slice(-4) : <span className="text-gray-300">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ข้อสังเกตรายแถว */}
            {issueRows.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 text-sm">
                <p className="font-medium text-amber-800 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4" /> ข้อสังเกต {issueRows.length} แถว (นำเข้าได้ตามปกติ — แก้ทีหลังได้)
                </p>
                <ul className="text-xs text-amber-700 mt-1.5 space-y-0.5 max-h-48 overflow-y-auto">
                  {issueRows.map((p) => (
                    <li key={p.emp_code}><span className="font-mono">{p.emp_code}</span> {p.nickname || p.first_name} {p.last_name} — {p.issues.join(' · ')}</li>
                  ))}
                </ul>
              </div>
            )}

            <button onClick={runImport} disabled={importM.isPending} className="btn-cta w-full justify-center flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> {importM.isPending ? 'กำลังนำเข้า…' : `ยืนยันนำเข้า ${parsed.length} คน`}
            </button>
          </div>
        )}

        {/* ผลลัพธ์ */}
        {result && (
          <div className="card p-4 space-y-2">
            <div className="flex items-center gap-2 text-emerald-700 font-medium">
              <CheckCircle2 className="w-5 h-5" /> นำเข้าสำเร็จ {result.written} คน
              {result.failed > 0 && <span className="text-red-600">· ไม่สำเร็จ {result.failed}</span>}
            </div>
            {result.errors.length > 0 && (
              <ul className="text-xs text-red-600 list-disc ml-4 max-h-40 overflow-y-auto">
                {result.errors.slice(0, 50).map((er, i) => <li key={i}><span className="font-mono">{er.emp_code}</span>: {er.msg}</li>)}
              </ul>
            )}
            <div className="flex gap-3 pt-1">
              <Link to="/hr/people#all" className="text-sm text-teal hover:underline inline-flex items-center gap-1"><Users className="w-4 h-4" /> ดูรายชื่อพนักงาน</Link>
              <Link to="/owner/comp" className="text-sm text-teal hover:underline">ไปตั้งฐานเงินเดือน →</Link>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: 'ok' | 'warn' | 'muted' }) {
  const color = tone === 'warn' ? 'text-amber-700' : tone === 'muted' ? 'text-gray-600' : 'text-teal-700';
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50/50 py-2">
      <div className={`text-lg font-semibold ${color}`}>{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}
